import React, { useEffect, useState } from 'react';
import { ComposableDeviceValues } from '../AddComposableDevice';
import { FormikProps } from 'formik';
import { FormControl, MenuItem, Stack } from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/store.model';
import {
  getMachinesTypeSelector,
  getMachineTypeSelector,
} from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { ErrorText } from '../../../../../theme/components/Forms';
import {
  StyledInputLabel,
  StyledSelectField,
  StyledStack,
  StyledTextField,
} from './StyledElements';
import { ImageForm } from './ImageForm';
import { IconUpload } from '../../../../../redux/groups/groups.model';

export const DeviceForm = ({
  formik,
  displayType = true,
  setImage,
  setLogo,
  image,
  logo,
  closeAddComposableDevice,
}: {
  formik: FormikProps<ComposableDeviceValues>;
  displayType?: boolean;
  setImage: React.Dispatch<React.SetStateAction<IconUpload | null>>;
  setLogo: React.Dispatch<React.SetStateAction<IconUpload | null>>;
  image: IconUpload | null;
  logo: IconUpload | null;
  closeAddComposableDevice: () => void;
}) => {
  const polyglot = getPolyglot();
  const machineTypes = useAppSelector(getMachinesTypeSelector);

  const isEditMode = Boolean(formik.values?.id);
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  const modelTypes = JSON.parse(
    machineType?.labels?.businessModelOptions || '[]'
  ) as {
    name: string;
    commandersQuantity: number;
  }[];

  const widthOptions = JSON.parse(
    machineType?.labels?.widthOptions || '[]'
  ) as number[];
  const depthOptions = JSON.parse(
    machineType?.labels?.depthOptions || '[]'
  ) as number[];

  const powerValuesKeys = JSON.parse(
    machineType?.labels?.POWER_VALUES || '{}'
  ) as Record<string, Record<string, Record<string, number>>>;

  const commandersQuantity =
    +(formik.values?.composableDevice?.labels?.commandersQuantity ?? 0) || 0;

  const [burnerDeckOptions, setBurnerDeckOptions] = useState<number[]>([]);
  const [commanderDecks, setCommanderDecks] =
    useState<number>(commandersQuantity);

  useEffect(() => {
    if (machineType?.name === 'OPTIMA') {
      const width = formik.values.composableDevice?.labels?.width || 0;
      const depth = formik.values.composableDevice?.labels?.depth || 0;
      const etageOptions = powerValuesKeys[depth]?.[width]?.['GAS_BURNER'] || 0;
      if (etageOptions) {
        const burnerDecks = Object.keys(etageOptions)
          .map((key) => Number(key))
          .filter((option) => option <= commanderDecks);
        setBurnerDeckOptions(burnerDecks);
      } else {
        setBurnerDeckOptions([]);
        if (width && depth) {
          formik.setFieldValue(
            'composableDevice.labels.electricalDecks',
            commanderDecks
          );
          formik.setFieldValue('composableDevice.labels.burnerDecks', 0);
        }
      }
    }
  }, [
    formik.values.composableDevice?.labels?.width,
    formik.values.composableDevice?.labels?.depth,
  ]);

  useEffect(() => {
    formik.setFieldValue('composableDevice.labels.burnerDecks', 0);
    formik.setFieldValue(
      'composableDevice.labels.electricalDecks',
      commanderDecks
    );
    setBurnerDeckOptions([]);

    formik.setFieldValue(
      'composableDevice.labels.width',
      formik.values?.composableDevice?.labels?.width
    );
    formik.setFieldValue(
      'composableDevice.labels.depth',
      formik.values?.composableDevice?.labels?.depth
    );
  }, [formik.values.composableDevice?.labels?.commandersQuantity]);
  return (
    <StyledStack>
      {displayType ? (
        <FormControl>
          <StyledInputLabel htmlFor="machine-type-select">
            {polyglot.t('composable_device.type')}
          </StyledInputLabel>
          <StyledSelectField
            onChange={(event) => {
              formik.handleChange(event);
              formik.setFieldValue(
                'composableDevice.machineTypeId',
                event.target.value
              );
            }}
            value={formik.values.composableDevice?.machineTypeId}
            hiddenLabel={true}
            id="machine-type-select"
            disabled={isEditMode}
          >
            {machineTypes.map((machineType) => (
              <MenuItem key={machineType.id} value={machineType.id}>
                {machineType?.name}
              </MenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
      ) : null}
      <FormControl>
        <StyledInputLabel htmlFor="name-input">
          {polyglot.t('composable_device.name')}*
        </StyledInputLabel>
        <StyledTextField
          name="name"
          type="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          required
          error={Boolean(formik.errors.name)}
          id="name-input"
          placeholder={polyglot.t('composable_device.enter_name')}
        />
        {formik.touched.name && formik.errors.name && (
          <ErrorText fontSize={'0.9rem'}>
            {polyglot.t(formik.errors.name)}
          </ErrorText>
        )}
      </FormControl>
      <FormControl>
        <StyledInputLabel htmlFor="model-select">
          {polyglot.t('composable_device.model')}
        </StyledInputLabel>
        <StyledSelectField
          onChange={(event) => {
            const selectedModel = modelTypes.find(
              (model) => model.name === event.target.value
            );
            formik.setFieldValue(
              'composableDevice.labels.businessModel',
              selectedModel?.name
            );

            formik.setFieldValue(
              'composableDevice.labels.commandersQuantity',
              selectedModel?.commandersQuantity
            );
            setCommanderDecks(selectedModel?.commandersQuantity || 0);
            formik.setFieldValue('composableDevice.labels.width', 0);
            formik.setFieldValue('composableDevice.labels.depth', 0);
            if (machineType?.name !== 'OPTIMA') {
              formik.setFieldValue(
                'composableDevice.labels.electricalDecks',
                selectedModel?.commandersQuantity
              );
              formik.setFieldValue('composableDevice.labels.burnerDecks', 0);
            }
          }}
          value={formik.values.composableDevice?.labels?.businessModel || ''}
          hiddenLabel={true}
          id="model-select"
          name="composableDevice.labels.businessModel"
        >
          {modelTypes.map((modelType) => (
            <MenuItem key={modelType.name} value={modelType.name}>
              {modelType?.name}
            </MenuItem>
          ))}
        </StyledSelectField>
      </FormControl>
      <Stack display={'grid'} gridTemplateColumns="1fr 1fr" gap={2}>
        <FormControl>
          <StyledInputLabel htmlFor="depth-select">
            {polyglot.t('composable_device.depth')}
          </StyledInputLabel>
          <StyledSelectField
            onChange={(event) => {
              const depth = depthOptions.find(
                (depth) => depth === event.target.value
              );

              formik.setFieldValue('composableDevice.labels.depth', depth);
            }}
            value={formik.values.composableDevice?.labels?.depth || ''}
            hiddenLabel={true}
            id="depth-select"
            name="composableDevice.labels.depth"
          >
            {depthOptions.map((depthOption) => (
              <MenuItem key={depthOption} value={depthOption}>
                {depthOption}
              </MenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
        <FormControl>
          <StyledInputLabel htmlFor="width-select">
            {polyglot.t('composable_device.width')}
          </StyledInputLabel>
          <StyledSelectField
            onChange={(event) => {
              const width = widthOptions.find(
                (width) => width === event.target.value
              );
              formik.setFieldValue('composableDevice.labels.width', width);
            }}
            value={formik.values.composableDevice?.labels?.width || ''}
            hiddenLabel={true}
            id="width-select"
            name="composableDevice.labels.width"
          >
            {widthOptions.map((widthOption) => (
              <MenuItem key={widthOption} value={widthOption}>
                {widthOption}
              </MenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
      </Stack>

      {machineType?.name === 'OPTIMA' && burnerDeckOptions.length ? (
        <FormControl>
          <StyledInputLabel htmlFor="burner-decks-select">
            {polyglot.t('composable_device.burnerDecks')}
          </StyledInputLabel>
          <StyledSelectField
            onChange={(event) => {
              const burnerDecks = burnerDeckOptions.find(
                (burnerDeck) => burnerDeck === event.target.value
              );

              formik.setFieldValue(
                'composableDevice.labels.burnerDecks',
                burnerDecks
              );
              formik.setFieldValue(
                'composableDevice.labels.electricalDecks',
                commanderDecks - (burnerDecks ?? 0)
              );
            }}
            value={formik.values.composableDevice?.labels?.burnerDecks || ''}
            hiddenLabel={true}
            id="burner-decks-select"
            name="composableDevice.labels.burnerDecks"
          >
            {burnerDeckOptions.map((burnerDeckOption) => (
              <MenuItem key={burnerDeckOption} value={burnerDeckOption}>
                {burnerDeckOption}
              </MenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
      ) : null}
      <FormControl>
        <StyledInputLabel htmlFor="description-input">
          {polyglot.t('composable_device.description')}
        </StyledInputLabel>
        <StyledTextField
          id="description-input"
          name="composableDevice.labels.description"
          type="description"
          multiline
          rows={4}
          onChange={formik.handleChange}
          value={formik.values.composableDevice?.labels?.description ?? ''}
          placeholder={polyglot.t('composable_device.enter_description')}
        />
      </FormControl>

      <ImageForm
        formik={formik}
        setImage={setImage}
        setLogo={setLogo}
        image={image}
        logo={logo}
        closeAddComposableDevice={closeAddComposableDevice}
      />
    </StyledStack>
  );
};
